import { type ReactNode, useEffect, useId, useRef } from "react";

import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";

interface BlockingModalProps {
  open: boolean;
  title: string;
  description: ReactNode;
  platformName: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
  showSwitchLocations: boolean;
  onSwitchLocations: () => void;
}

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

let bodyScrollLockCount = 0;
let previousBodyOverflow: string | null = null;

const lockBodyScroll = () => {
  if (bodyScrollLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  bodyScrollLockCount += 1;
};

const unlockBodyScroll = () => {
  if (bodyScrollLockCount === 0) {
    return;
  }

  bodyScrollLockCount -= 1;

  if (bodyScrollLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow ?? "";
    previousBodyOverflow = null;
  }
};

export const BlockingModal = ({
  open,
  title,
  description,
  platformName,
  contactPhone,
  contactEmail,
  showSwitchLocations,
  onSwitchLocations,
}: BlockingModalProps) => {
  const { t } = useTranslation();
  const dialogId = useId();
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    let didLockBodyScroll = false;

    if (open) {
      lockBodyScroll();
      didLockBodyScroll = true;

      // Focus the first focusable element inside the modal.
      // This prevents tabbing into background elements on web.
      queueMicrotask(() => {
        const root = modalRef.current;
        if (!root) return;

        const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        (focusables[0] ?? root).focus();
      });
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key !== "Tab") return;

      const root = modalRef.current;
      if (!root) return;

      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
      );

      if (focusables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!active) return;

      if (!root.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      if (didLockBodyScroll) {
        unlockBodyScroll();
      }

      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const handleContactClick = () => {
    if (contactPhone) {
      const normalizedPhone = contactPhone.replace(/[^\d+]/g, "");
      window.location.href = `tel:${normalizedPhone}`;
    } else if (contactEmail) {
      window.location.href = `mailto:${contactEmail}`;
    }
  };

  const showPrimaryCTA = !!(contactPhone || contactEmail);

  return (
    <>
      <div
        className="fixed inset-0 z-50"
        style={{ backgroundColor: "rgba(217, 217, 217, 0.6)" }}
        aria-hidden="true"
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogId}
          tabIndex={-1}
          className="w-full max-w-[90%] rounded-2xl bg-white shadow-xl sm:max-w-[350px]"
        >
          <div className="flex flex-col gap-6 px-8 py-8">
            <h1 id={dialogId} className="font-serif text-[24px] italic leading-tight text-black">
              {title}
            </h1>
            <div className="flex flex-col gap-3 text-center text-sm leading-[18px] text-secondary">
              {description}
            </div>
          </div>

          <div className="flex w-full flex-col gap-4 px-8 pb-8">
            {showPrimaryCTA && (
              <Button fullWidth onClick={handleContactClick}>
                {t("blockingModal.contactPlatform", { platformName })}
              </Button>
            )}
            {showSwitchLocations && (
              <Button fullWidth style="secondary" onClick={onSwitchLocations}>
                {t("blockingModal.switchLocations")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
